import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const InfoCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: auto;
  flex: 1;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor || `var(--color-secondary)`};
  font-family: var(--font-paragraphs);
  padding: ${({ variant }) => (variant === "column" ? "2rem 1rem" : "1rem")};
  margin: 1rem 2px;
  align-items: ${({ alignContent }) => alignContent};
  border-right: ${({ variant }) =>
    variant === "column" ? "none" : "2px solid #C3C3C3"};
  justify-content: ${({ alignContent, variant }) =>
    variant === "column" ? "flex-start" : alignContent};
  &:last-of-type {
    border-right: none;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 100%;
    flex: unset;
    padding: 1.5rem 0.5rem;
    margin: 0;
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-flow: ${({ variant }) =>
    variant === "column" ? "column wrap" : "row nowrap"};
`;
const Heading = styled.p`
  font-size: ${({ variant }) => (variant === "column" ? "1rem" : "1.3rem")};
  font-weight: 600;
  padding: ${({ variant }) =>
    variant === "column" ? "0 1.5rem" : "0 1.5rem 0 0"};
  margin: auto 0;
  width: auto;
  text-align: start;
`;
const TextWrapper = styled.div`
  display: flex;
  flex-flow: ${({ variant }) =>
    variant === "column" ? "column wrap" : "row nowrap"};
  color: ${({ textColorHex }) => textColorHex || `var(--color-body-text)`};
  align-items: ${({ alignContent, variant }) =>
    variant === "column" ? alignContent : "center"};
  justify-content: ${({ alignContent }) => alignContent};
`;
const InfoText = styled.p`
  font-size: 0.9rem;
  padding: ${({ variant }) =>
    variant === "column" ? "0 1.5rem" : "0 1.5rem 0 0"};
  margin: 0;
  text-align: ${({ alignContent }) =>
    alignContent === "flex-end"
      ? "end"
      : alignContent === "flex-start"
      ? "start"
      : alignContent === "center"
      ? "center"
      : "start"};
`;
const IconWrapper = styled.div`
  display: flex;
  justify-content: ${({ alignContent }) => alignContent};
  padding: ${({ variant }) =>
    variant === "column" ? "0 2rem 1.5rem 2rem" : "0 10px 0 0"};
  align-items: center;
`;
const Icon = styled(FontAwesomeIcon)`
  font-size: ${({ variant }) =>
    variant === "column" ? "2rem!important" : "1rem!important"};
  color: ${({ iconColor }) => iconColor || `var(--color-primary)`};
`;

const InfoCardWrappeLink = styled.a`
  display: flex;
  flex-direction: column;
  width: auto;
  flex: 1;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor || `var(--color-secondary)`};
  font-family: var(--font-paragraphs);
  padding: ${({ variant }) => (variant === "column" ? "2rem 1rem" : "1rem")};
  margin: 1rem 2px;
  align-items: ${({ alignContent }) => alignContent};
  border-right: ${({ variant }) =>
    variant === "column" ? "none" : "2px solid #C3C3C3"};
  justify-content: ${({ alignContent, variant }) =>
    variant === "column" ? "flex-start" : alignContent};
  text-decoration: none;
  transition: 0.3s all cubic-bezier(0.445, 0.05, 0.55, 0.95);
  &:last-of-type {
    border-right: none;
  }
  &:hover {
    transition: 0.3s all cubic-bezier(0.445, 0.05, 0.55, 0.95);
    background: var(--color-dark-grey);
    color: white !important;
    ${Icon} {
      transition: 0.3s all cubic-bezier(0.445, 0.05, 0.55, 0.95);
      color: white !important;
    }
    ${Heading} {
      transition: 0.3s all cubic-bezier(0.445, 0.05, 0.55, 0.95);
      color: white !important;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 100%;
    flex: unset;
    padding: 1.5rem 0.5rem;
    margin: 0;
  }
`;

export default function InfoCard({
  title,
  text,
  icon,
  url,
  variant,
  alignContent,
  backgroundColorHex,
  textColorHex,
  iconColorHex,
}) {
  return (
    <>
      {url ? (
        <>
          <InfoCardWrappeLink
            variant={variant}
            backgroundColor={backgroundColorHex}
            alignContent={alignContent}
            textColor={textColorHex}
            href={url}
          >
            <InnerWrapper variant={variant}>
              {icon && (
                <IconWrapper alignContent={alignContent} variant={variant}>
                  <Icon
                    variant={variant}
                    icon={icon}
                    iconColor={iconColorHex}
                  />{" "}
                </IconWrapper>
              )}

              <TextWrapper
                variant={variant}
                textColorHex={textColorHex}
                alignContent={alignContent}
              >
                {title && <Heading variant={variant}>{title}</Heading>}
                {text && (
                  <InfoText alignContent={alignContent} variant={variant}>
                    {text}
                  </InfoText>
                )}
              </TextWrapper>
            </InnerWrapper>
          </InfoCardWrappeLink>
        </>
      ) : (
        <InfoCardWrapper
          variant={variant}
          backgroundColor={backgroundColorHex}
          alignContent={alignContent}
          textColor={textColorHex}
        >
          <InnerWrapper variant={variant}>
            {icon && (
              <IconWrapper alignContent={alignContent} variant={variant}>
                <Icon variant={variant} icon={icon} iconColor={iconColorHex} />{" "}
              </IconWrapper>
            )}

            <TextWrapper
              variant={variant}
              textColorHex={textColorHex}
              alignContent={alignContent}
            >
              {title && <Heading variant={variant}>{title}</Heading>}
              {text && (
                <InfoText alignContent={alignContent} variant={variant}>
                  {text}
                </InfoText>
              )}
            </TextWrapper>
          </InnerWrapper>
        </InfoCardWrapper>
      )}
    </>
  );
}

InfoCard.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string,
  icon: PropTypes.object,
  url: PropTypes.string,
  variant: PropTypes.string,
  alignContent: PropTypes.string,
  backgroundColorHex: PropTypes.string,
  textColorHex: PropTypes.string,
  iconColorHex: PropTypes.string,
};
InfoCard.defaultProps = {
  title: "",
  text: "",
};
